import React, { useState, useRef, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Loader2, Trash2, Bot, User, Sparkles, Zap, Brain, Dumbbell, Timer } from 'lucide-react';
import { API } from '../../lib/api';

const CHAT_SESSION_KEY = 'ninjathlete_chat_session';

// Context-aware suggestions based on current page
const getPageContext = (pathname) => {
  if (pathname.includes('/hyrox')) {
    return {
      greeting: "I see you're exploring HYROX! Ask me about race prep, station strategies, or training plans.",
      suggestions: [
        { label: "HYROX pacing strategy", icon: Timer },
        { label: "Ski erg technique tips", icon: Dumbbell },
        { label: "8-week training plan", icon: Sparkles }
      ]
    };
  }
  if (pathname.includes('/blog') && pathname.includes('meditation')) {
    return {
      greeting: "Looking into meditation? I can guide you through different techniques and protocols.",
      suggestions: [
        { label: "Box breathing guide", icon: Brain },
        { label: "Best time to meditate", icon: Timer },
        { label: "Meditation for focus", icon: Sparkles }
      ]
    };
  }
  if (pathname.includes('/blog')) {
    return {
      greeting: "Browsing the blog? Ask me to explain any concept or go deeper on a topic!",
      suggestions: [
        { label: "Cold exposure benefits", icon: Zap },
        { label: "Sleep optimization tips", icon: Brain },
        { label: "Biohacking basics", icon: Sparkles }
      ]
    };
  }
  if (pathname.includes('/shop')) {
    return {
      greeting: "Shopping for gear? I can help you choose the right products for your goals.",
      suggestions: [
        { label: "Best supplements for focus", icon: Brain },
        { label: "Recovery essentials", icon: Dumbbell },
        { label: "What gear do I need?", icon: Sparkles }
      ]
    };
  }
  if (pathname.includes('/timers')) {
    return {
      greeting: "Using the timers? Ask me about optimal intervals, breathing patterns, or protocols!",
      suggestions: [
        { label: "Wim Hof breathing guide", icon: Brain },
        { label: "Pomodoro for deep work", icon: Timer },
        { label: "HIIT interval setup", icon: Zap }
      ]
    };
  }
  // Default homepage context
  return {
    greeting: "Hey! I'm NinjaBot, your wellness AI coach. Ask me about biohacking, meditation, HYROX training, nutrition, or fitness. How can I help you optimize today?",
    suggestions: [
      { label: "Morning routine protocol", icon: Sparkles },
      { label: "HYROX race tips", icon: Dumbbell },
      { label: "Biohacking for beginners", icon: Zap }
    ]
  };
};

export const AIChatWidget = () => {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [hasInteracted, setHasInteracted] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  const pageContext = getPageContext(location.pathname);

  // Load session on mount
  useEffect(() => {
    const storedSession = localStorage.getItem(CHAT_SESSION_KEY);
    if (storedSession) {
      setSessionId(storedSession);
      loadChatHistory(storedSession);
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const loadChatHistory = async (sid) => {
    try {
      const response = await fetch(`${API}/chat/${sid}/history`);
      if (response.ok) {
        const data = await response.json();
        if (data.messages && data.messages.length > 0) {
          setMessages(data.messages);
          setHasInteracted(true);
        }
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const sendMessage = async (messageText = null) => {
    const userMessage = (messageText || inputValue).trim();
    if (!userMessage || isLoading) return;

    setInputValue('');
    setIsLoading(true);
    setHasInteracted(true);

    // Add user message immediately
    const newUserMessage = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, newUserMessage]);

    try {
      // Include page context in the message for better awareness
      const contextualMessage = !hasInteracted 
        ? `[User is on page: ${location.pathname}] ${userMessage}`
        : userMessage;

      const response = await fetch(`${API}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          session_id: sessionId,
          message: contextualMessage
        })
      });

      if (!response.ok) {
        throw new Error('Chat request failed');
      }

      const data = await response.json();
      
      // Save session ID
      if (!sessionId && data.session_id) {
        setSessionId(data.session_id);
        localStorage.setItem(CHAT_SESSION_KEY, data.session_id);
      }

      // Add assistant message
      const assistantMessage = { role: 'assistant', content: data.response };
      setMessages(prev => [...prev, assistantMessage]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.',
        isError: true
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = async () => {
    if (sessionId) {
      try {
        await fetch(`${API}/chat/${sessionId}`, { method: 'DELETE' });
      } catch (error) {
        console.error('Failed to clear session:', error);
      }
    }
    setMessages([]);
    setSessionId(null);
    setHasInteracted(false);
    localStorage.removeItem(CHAT_SESSION_KEY);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleSuggestionClick = (suggestion) => {
    sendMessage(suggestion);
  };

  return (
    <>
      {/* Chat Button - Floating with pulse animation */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-40 w-14 h-14 rounded-full bg-gradient-to-br from-[#E63946] to-[#c62d3a] text-white flex items-center justify-center shadow-lg shadow-red-900/30 hover:shadow-red-900/50 hover:scale-110 transition-all duration-300 group ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="chat-widget-button"
        aria-label="Open chat"
      >
        <MessageCircle className="w-6 h-6 group-hover:scale-110 transition-transform" />
        {/* Pulse ring */}
        <span className="absolute inset-0 rounded-full bg-[#E63946] animate-ping opacity-25" />
      </button>

      {/* Chat Window */}
      <div 
        className={`fixed bottom-6 right-6 z-50 w-[400px] max-w-[calc(100vw-24px)] sm:max-w-[calc(100vw-48px)] h-[550px] sm:h-[600px] max-h-[calc(100vh-100px)] bg-gradient-to-b from-[#0c0c0c] to-[#0A0A0A] border border-zinc-800/80 rounded-2xl shadow-2xl shadow-black/50 flex flex-col overflow-hidden transition-all duration-300 ${isOpen ? 'scale-100 opacity-100' : 'scale-95 opacity-0 pointer-events-none'}`}
        data-testid="chat-widget-window"
      >
        {/* Header with gradient accent */}
        <div className="relative flex items-center justify-between px-4 py-3 bg-zinc-900/90 border-b border-zinc-800">
          <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-[#E63946]/50 to-transparent" />
          <div className="flex items-center gap-3">
            <div className="relative w-10 h-10 rounded-full bg-gradient-to-br from-[#E63946] to-[#c62d3a] flex items-center justify-center">
              <Bot className="w-5 h-5 text-white" />
              {/* Online indicator */}
              <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-green-500 rounded-full border-2 border-zinc-900" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm tracking-wide">NinjaBot</h3>
              <p className="text-zinc-500 text-xs">Wellness AI Coach • Online</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={clearChat}
              className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-all"
              title="Clear chat"
              data-testid="clear-chat-btn"
            >
              <Trash2 className="w-4 h-4" />
            </button>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 text-zinc-500 hover:text-zinc-300 hover:bg-zinc-800 rounded-lg transition-all"
              data-testid="close-chat-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin scrollbar-thumb-zinc-700 scrollbar-track-transparent">
          {/* Welcome/Context Message */}
          {messages.length === 0 && (
            <div className="space-y-4">
              <div className="flex gap-3">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#c62d3a] flex-shrink-0 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-white" />
                </div>
                <div className="max-w-[85%] bg-zinc-800/80 rounded-2xl rounded-tl-sm px-4 py-3 border border-zinc-700/50">
                  <p className="text-sm text-zinc-200 leading-relaxed">
                    {pageContext.greeting}
                  </p>
                </div>
              </div>
              
              {/* Quick Suggestion Chips */}
              <div className="pl-11 space-y-2">
                <p className="text-xs text-zinc-500 font-medium uppercase tracking-wider">Quick questions</p>
                <div className="flex flex-wrap gap-2">
                  {pageContext.suggestions.map((suggestion, idx) => (
                    <button
                      key={suggestion.label}
                      onClick={() => handleSuggestionClick(suggestion.label)}
                      className="flex items-center gap-2 px-3 py-2 bg-zinc-800/60 hover:bg-zinc-700/80 border border-zinc-700/50 hover:border-[#E63946]/50 rounded-full text-xs text-zinc-300 hover:text-white transition-all group"
                      data-testid={`suggestion-${idx}`}
                    >
                      <suggestion.icon className="w-3 h-3 text-[#E63946] group-hover:scale-110 transition-transform" />
                      {suggestion.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Message List */}
          {messages.map((message, index) => (
            <div
              key={message.isError}
              className={`flex gap-3 animate-fadeIn ${message.role === 'user' ? 'flex-row-reverse' : ''}`}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {/* Avatar */}
              <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                message.role === 'user' 
                  ? 'bg-zinc-700' 
                  : 'bg-gradient-to-br from-[#E63946] to-[#c62d3a]'
              }`}>
                {message.role === 'user' 
                  ? <User className="w-4 h-4 text-white" />
                  : <Bot className="w-4 h-4 text-white" />
                }
              </div>
              
              {/* Message Bubble */}
              <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                message.role === 'user'
                  ? 'bg-gradient-to-br from-[#E63946] to-[#c62d3a] text-white rounded-tr-sm shadow-lg shadow-red-900/20'
                  : message.isError
                  ? 'bg-red-950/50 text-red-300 rounded-tl-sm border border-red-900/50'
                  : 'bg-zinc-800/80 text-zinc-200 rounded-tl-sm border border-zinc-700/50'
              }`}>
                <p className="text-sm leading-relaxed whitespace-pre-wrap">
                  {message.content}
                </p>
              </div>
            </div>
          ))}
          
          {/* Typing Indicator */}
          {isLoading && (
            <div className="flex gap-3 animate-fadeIn">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#E63946] to-[#c62d3a] flex items-center justify-center">
                <Bot className="w-4 h-4 text-white" />
              </div>
              <div className="bg-zinc-800/80 rounded-2xl rounded-tl-sm px-4 py-3 border border-zinc-700/50">
                <div className="flex items-center gap-1">
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                  <span className="w-2 h-2 bg-zinc-500 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                </div>
              </div>
            </div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="p-3 sm:p-4 border-t border-zinc-800 bg-zinc-900/80">
          <div className="flex items-center gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask about biohacking, HYROX, meditation..."
              className="flex-1 bg-zinc-800/80 border border-zinc-700 rounded-full px-4 py-3 text-sm text-white placeholder-zinc-500 focus:border-[#E63946] focus:ring-1 focus:ring-[#E63946]/50 focus:outline-none transition-all"
              disabled={isLoading}
              data-testid="chat-input"
            />
            <button
              onClick={() => sendMessage()}
              disabled={!inputValue.trim() || isLoading}
              className="w-10 h-10 sm:w-11 sm:h-11 rounded-full bg-gradient-to-br from-[#E63946] to-[#c62d3a] text-white flex items-center justify-center hover:shadow-lg hover:shadow-red-900/30 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none transition-all"
              data-testid="send-message-btn"
            >
              {isLoading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </button>
          </div>
          <p className="text-[10px] text-zinc-600 text-center mt-2">
            Powered by NinjAthlete AI
          </p>
        </div>
      </div>

      {/* Global styles for animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out forwards;
        }
      `}</style>
    </>
  );
};

export default AIChatWidget;
